import { clsx } from 'clsx';
import { Info } from 'lucide-react';
import type { ReactNode } from 'react';
import Space from '@/components/common/Space';
import Tooltip from '@/components/common/Tooltip';

export interface FieldProps {
    name: string;
    id: string;
    label?: ReactNode;
    description?: ReactNode;
    helpElement?: ReactNode;
    errorElement?: ReactNode;
    labelExtra?: ReactNode;
    tooltipElement?: ReactNode;
    children: ReactNode;
    className?: string;
    required?: boolean;
    /** Font size for label and description. Defaults to 'base' (16px) */
    labelSize?: 'sm' | 'base';
    /** Font weight for label. Defaults to 'semibold' */
    labelWeight?: 'medium' | 'semibold';
    /** Whether to capitalize the label. Defaults to true */
    labelCapitalize?: boolean;
    disabled?: boolean;
    showGap?: boolean;
    /** Display label and input inline. Defaults to false */
    inline?: boolean;
}

const Field = ({
    name,
    id,
    label,
    description,
    labelExtra,
    tooltipElement,
    helpElement,
    errorElement,
    children,
    className,
    required,
    labelSize = 'base',
    labelWeight = 'semibold',
    labelCapitalize = true,
    disabled,
    showGap = true,
    inline = false,
}: FieldProps) => {
    let labelElement: ReactNode = null;
    const labelTextSize = labelSize === 'base' ? 'text-base' : 'text-sm';
    const labelFontWeight = labelWeight === 'semibold' ? 'font-semibold' : 'font-medium';

    if (label) {
        labelElement = (
            <>
                <label
                    className={clsx(
                        'inline-flex items-center gap-1 text-left leading-6 text-neutral-900',
                        labelTextSize,
                        labelFontWeight
                    )}
                    htmlFor={id}
                >
                    <span className={labelCapitalize ? 'capitalize' : ''}>{label}</span>
                    {required && <span className="text-destructive ml-1">*</span>}
                    {tooltipElement && (
                        <Tooltip title={tooltipElement}>
                            <Info className="text-muted-foreground size-4 cursor-pointer" />
                        </Tooltip>
                    )}
                </label>
                {description && (
                    <>
                        <span className={clsx('text-muted-foreground font-medium', labelTextSize)}>{description}</span>
                    </>
                )}
            </>
        );

        if (labelExtra) {
            labelElement = (
                <div className="flex items-center justify-between">
                    {labelElement}
                    {labelExtra}
                </div>
            );
        }
    }

    if (inline) {
        return (
            <div className={className}>
                <Space
                    className="w-full"
                    direction="horizontal"
                    gapSize={5}
                    data-input-name={name}
                    aria-disabled={disabled || undefined}
                >
                    <div className="shrink-0">{labelElement}</div>
                    <div className="flex-1">{children}</div>
                </Space>
                {errorElement && <p className="text-destructive mt-1 text-base">{errorElement}</p>}
                {helpElement && <p className="text-muted-foreground mt-1 text-base font-medium">{helpElement}</p>}
            </div>
        );
    }

    return (
        <Space
            className={clsx(className, 'w-full')}
            direction="vertical"
            gapSize={showGap ? 1.5 : 0}
            data-input-name={name}
            aria-disabled={disabled || undefined}
        >
            {labelElement}
            {children}
            {errorElement && <p className="text-destructive text-base">{errorElement}</p>}
            {helpElement && <p className="text-muted-foreground text-base font-medium">{helpElement}</p>}
        </Space>
    );
};

export default Field;
